/**
 * Onboarding Template Picker — /onboarding/website-template
 *
 * Shown after a customer onboards. Lists all 20 active real-estate
 * templates from /api/website-templates. Selecting one opens a paywall
 * confirmation modal showing the $29/month subscription + optional $49
 * custom domain setup fee. POSTs to /api/customer-websites/select-template
 * which gates trial/active/card-on-file checks and charges the saved card
 * via Payment Hub.
 *
 * Query params: ?agent_id=…&tenant_id=…&slug=…&token=…
 */
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, ExternalLink, Loader2, Sparkles, AlertCircle,
  CreditCard, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICE_SUB     = 29;     // $29/month
const PRICE_DOMAIN  = 49;     // $49 one-time

export default function OnboardingWebsiteTemplatePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const agent_id  = params.get('agent_id') || '';
  const tenant_id = params.get('tenant_id') || '';
  const slug      = params.get('slug') || '';
  const token     = params.get('token') || '';

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenStatus, setTokenStatus] = useState('checking'); // 'ok' | 'expired' | 'missing'
  const [submitting, setSubmitting] = useState('');
  const [chosen, setChosen] = useState(null);
  const [paywallError, setPaywallError] = useState(null);

  // Confirmation modal state
  const [confirm, setConfirm] = useState({ open: false, template_id: '', name: '' });
  const [domainSetup, setDomainSetup] = useState(false);

  // Validate token on mount (if provided)
  useEffect(() => {
    if (!token) {
      setTokenStatus(agent_id ? 'ok' : 'missing');
      return;
    }
    (async () => {
      try {
        await axios.get(`${API}/onboarding-token/verify?token=${encodeURIComponent(token)}`);
        setTokenStatus('ok');
      } catch {
        setTokenStatus('expired');
      }
    })();
  }, [token, agent_id]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/website-templates`);
        setTemplates(r.data.templates || []);
      } catch { toast.error('Could not load templates'); }
      setLoading(false);
    })();
  }, []);

  const grid = useMemo(() => templates, [templates]);

  const openConfirm = (t) => {
    setPaywallError(null);
    setConfirm({ open: true, template_id: t.template_id, name: t.name });
    setDomainSetup(false);
  };

  const closeConfirm = () => setConfirm({ open: false, template_id: '', name: '' });

  const submit = async () => {
    if (!agent_id) {
      toast.error('Missing agent_id — open this page from the onboarding flow.');
      return;
    }
    setSubmitting(confirm.template_id);
    setPaywallError(null);
    try {
      const r = await axios.post(`${API}/customer-websites/select-template`, {
        template_id: confirm.template_id,
        agent_id, tenant_id: tenant_id || undefined,
        slug: slug || undefined,
        custom_domain_setup: domainSetup,
        token: token || undefined,
      });
      setChosen(r.data);
      closeConfirm();
      const charged = r.data?.subscription?.skipped === false;
      toast.success(charged
        ? `Charged $${PRICE_SUB} — website published with ${confirm.template_id}`
        : `Template switched to ${confirm.template_id} (no extra charge)`);
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data || {};
      if (status === 402 || status === 403) {
        setPaywallError({ code: data.code, message: data.message });
      } else if (status === 401) {
        setTokenStatus('expired');
        closeConfirm();
      } else {
        toast.error(data.detail || data.message || e.message);
      }
    }
    setSubmitting('');
  };

  if (tokenStatus === 'expired') {
    return <ExpiredTokenScreen />;
  }
  if (tokenStatus === 'missing') {
    return <MissingContextScreen />;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="template-picker-page">
      <Helmet>
        <title>Choose your website style · Agent9</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex items-start justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-1 flex items-center gap-1.5">
              <Sparkles size={12} /> Final step
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Choose your website style
            </h1>
            <p className="text-sm text-slate-400 mt-2 max-w-2xl">
              Pick a layout that matches your brand. Website Builder is
              <span className="text-slate-200 font-medium"> ${PRICE_SUB}/month</span> charged
              to your card on file. Switching templates later is free.
            </p>
          </div>
          {chosen && (
            <a href={chosen.preview_url} target="_blank" rel="noreferrer"
               data-testid="open-published-site"
               className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300">
              View site <ExternalLink size={14} />
            </a>
          )}
        </div>

        {loading && (
          <div className="flex justify-center py-20" data-testid="template-loading">
            <Loader2 className="animate-spin text-slate-500" size={28} />
          </div>
        )}

        {!loading && grid.length === 0 && (
          <p className="text-center py-20 text-slate-400">No templates available.</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {grid.map((t) => (
            <div key={t.template_id}
                 data-testid={`tpl-card-${t.template_id}`}
                 className={`rounded-xl overflow-hidden border bg-slate-900/60 transition hover:border-slate-700 ${
                   chosen?.template_id === t.template_id ? 'border-emerald-500' : 'border-slate-800'
                 }`}>
              <div className="aspect-[4/3] bg-slate-950">
                <img src={`${API}/website-templates/${t.template_id}/preview.svg`}
                     alt={`Preview of ${t.name}`}
                     loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <p className="font-semibold">{t.name}</p>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{t.description}</p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(t.layout_json?.sections || []).slice(0, 4).map((s) => (
                    <span key={s} className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-slate-800 text-slate-300">
                      {s}
                    </span>
                  ))}
                </div>
                <Button
                  data-testid={`tpl-use-${t.template_id}`}
                  disabled={!!submitting}
                  onClick={() => openConfirm(t)}
                  className={`w-full ${
                    chosen?.template_id === t.template_id
                      ? 'bg-emerald-600 hover:bg-emerald-500'
                      : 'bg-slate-800 hover:bg-slate-700'
                  }`}>
                  {submitting === t.template_id
                    ? <><Loader2 size={14} className="mr-1 animate-spin"/>Publishing…</>
                    : chosen?.template_id === t.template_id
                      ? <><CheckCircle2 size={14} className="mr-1"/>Selected — published</>
                      : 'Use this template'}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {chosen && (
          <div className="mt-10 rounded-xl bg-emerald-500/10 border border-emerald-500/40 p-5"
               data-testid="published-banner">
            <p className="font-semibold mb-1 flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-400" />
              Your website is live
            </p>
            <p className="text-sm text-slate-300">
              Template: <span className="font-mono">{chosen.template_id}</span> ·{' '}
              <a href={chosen.preview_url} target="_blank" rel="noreferrer"
                 className="underline text-emerald-300">{chosen.preview_url}</a>
            </p>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => navigate('/dashboard')} variant="secondary">
                Go to dashboard
              </Button>
              <Button variant="ghost" onClick={() => setChosen(null)}>
                Pick a different one
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Paywall confirmation dialog */}
      <Dialog open={confirm.open} onOpenChange={(o) => !o && closeConfirm()}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100"
                       data-testid="paywall-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={16} /> Confirm purchase
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              You're about to publish <span className="font-medium text-slate-200">{confirm.name}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <PriceRow
              label="Website Builder subscription"
              detail="Billed to your card on file today, then monthly. Cancel anytime."
              price={`$${PRICE_SUB}/mo`}
            />
            <label className="flex items-start gap-3 rounded-lg border border-slate-800 p-3 cursor-pointer hover:border-slate-700"
                   data-testid="domain-setup-toggle">
              <input type="checkbox" checked={domainSetup}
                     onChange={(e) => setDomainSetup(e.target.checked)}
                     className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Custom domain setup</p>
                  <p className="text-sm font-mono">${PRICE_DOMAIN} one-time</p>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  We'll wire your custom domain ({slug ? `e.g. ${slug}.com` : 'yourbrand.com'}) with SSL.
                  Skip if you'll use Agent9's free subdomain.
                </p>
              </div>
            </label>

            {paywallError && (
              <PaywallErrorBanner code={paywallError.code} message={paywallError.message} />
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={closeConfirm}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={!!submitting}
              data-testid="confirm-pay-btn"
              className="bg-emerald-600 hover:bg-emerald-500">
              {submitting
                ? <><Loader2 size={14} className="mr-1 animate-spin"/>Charging card…</>
                : <><CreditCard size={14} className="mr-1"/> Confirm & Pay</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── Sub-components ────────────────────────────────────────────────────────


function PriceRow({ label, detail, price }) {
  return (
    <div className="rounded-lg border border-slate-800 p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-sm font-mono text-emerald-400">{price}</p>
      </div>
      <p className="text-xs text-slate-400 mt-0.5">{detail}</p>
    </div>
  );
}

function PaywallErrorBanner({ code, message }) {
  let cta = null;
  if (code === 'CARD_ON_FILE_REQUIRED' || code === 'WEBSITE_PAYMENT_FAILED') {
    cta = <Link to="/dashboard/billing" className="underline text-emerald-300">Add card on file →</Link>;
  } else if (code === 'TRIAL_ACCOUNT_NOT_ALLOWED') {
    cta = <Link to="/dashboard/billing" className="underline text-emerald-300">Activate paid account →</Link>;
  }
  return (
    <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm space-y-1"
         data-testid="paywall-error">
      <p className="flex items-center gap-2 font-medium text-rose-200">
        <AlertCircle size={14} /> {code?.replace(/_/g, ' ')}
      </p>
      <p className="text-rose-100/90">{message}</p>
      {cta && <p className="pt-1">{cta}</p>}
    </div>
  );
}

function ExpiredTokenScreen() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6"
         data-testid="token-expired-screen">
      <div className="max-w-md text-center space-y-5">
        <AlertCircle size={48} className="text-amber-400 mx-auto" />
        <h1 className="text-2xl font-bold">Onboarding link expired</h1>
        <p className="text-sm text-slate-400">
          For security, the link from your onboarding email expires after 30 minutes.
          Open the admin dashboard to resend it, or pick a template from inside your account.
        </p>
        <div className="flex justify-center gap-2">
          <Button asChild><Link to="/dashboard/website/templates">Go to dashboard</Link></Button>
          <Button asChild variant="secondary"><Link to="/admin/onboard">Back to admin</Link></Button>
        </div>
      </div>
    </div>
  );
}

function MissingContextScreen() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6"
         data-testid="token-missing-screen">
      <div className="max-w-md text-center space-y-5">
        <AlertCircle size={48} className="text-slate-400 mx-auto" />
        <h1 className="text-2xl font-bold">Open this from onboarding</h1>
        <p className="text-sm text-slate-400">
          We need your agent context to publish a website. Please open this page
          from the link sent during onboarding, or pick a template from inside
          your account.
        </p>
        <Button asChild><Link to="/dashboard/website/templates">Go to dashboard</Link></Button>
      </div>
    </div>
  );
}
